import { useState } from "react";
import { createPortal } from "react-dom";
import { Printer, Star } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useT } from "@/i18n";

const THEMES = [
  { id: "sun", bg: "from-yellow-200 via-amber-200 to-orange-300", title: "text-orange-600", es: "Sol", en: "Sun" },
  { id: "candy", bg: "from-pink-200 via-rose-200 to-fuchsia-300", title: "text-fuchsia-700", es: "Dulce", en: "Candy" },
  { id: "sky", bg: "from-sky-200 via-cyan-200 to-teal-300", title: "text-blue-700", es: "Cielo", en: "Sky" },
];

const TX = {
  es: { title: "Cartel de mi negocio", sub: "Escribe el precio y dónde vendes, elige colores e imprímelo.", price: "Precio", priceVal: "$1", where: "Dónde y cuándo", whereVal: "Frente a mi casa, sábado 10am", print: "Imprimir cartel", open: "¡Abierto!", by: "Pide ayuda a un adulto para imprimir." },
  en: { title: "My business poster", sub: "Write the price and where you sell, pick colors and print it.", price: "Price", priceVal: "$1", where: "Where and when", whereVal: "In front of my house, Saturday 10am", print: "Print poster", open: "Open!", by: "Ask an adult to help you print." },
};

const Poster = ({ idea, theme, price, where, tx, className = "", tid = "poster" }) => (
  <div className={`bg-gradient-to-br ${theme.bg} rounded-3xl border-8 border-white p-6 flex flex-col items-center text-center aspect-[3/4] ${className}`} data-testid={`${tid}-preview`}>
    <span className="bg-white rounded-full px-5 py-1 font-fredoka font-bold text-rose-500 text-lg shadow inline-flex items-center gap-1"><Star className="w-4 h-4 fill-yellow-300 text-yellow-400" /> {tx.open}</span>
    <h2 className={`font-fredoka font-bold ${theme.title} text-4xl leading-tight mt-4 break-words w-full`} data-testid={`${tid}-name`}>{idea.name}</h2>
    {idea.slogan && <p className="font-bold text-blue-950 text-lg mt-2" data-testid={`${tid}-slogan`}>{idea.slogan}</p>}
    <img src="/img/lumi.webp" alt="" className="flex-1 min-h-0 w-auto object-contain my-3 drop-shadow-xl" />
    <p className="font-fredoka font-bold text-blue-950 text-xl">{idea.product}</p>
    {price && <p className="bg-white rounded-2xl px-6 py-2 font-fredoka font-bold text-4xl text-green-600 mt-2 shadow" data-testid={`${tid}-price`}>{price}</p>}
    {where && <p className="font-bold text-blue-950 mt-3" data-testid={`${tid}-where`}>{where}</p>}
  </div>
);

export default function BusinessPoster({ idea, onClose }) {
  const { lang } = useT();
  const tx = TX[lang] || TX.es;
  const [themeId, setThemeId] = useState("sun");
  const [price, setPrice] = useState(tx.priceVal);
  const [where, setWhere] = useState(tx.whereVal);
  const theme = THEMES.find((t) => t.id === themeId);
  const props = { idea, theme, price, where, tx };

  return (
    <Dialog open={!!idea} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-[2rem] border-4 border-yellow-300 max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="poster-dialog">
        <DialogTitle className="font-fredoka text-2xl font-bold text-blue-950">{tx.title}</DialogTitle>
        <DialogDescription className="font-semibold text-blue-900">{tx.sub}</DialogDescription>
        <div className="grid sm:grid-cols-2 gap-5">
          <Poster {...props} className="max-h-[60vh] mx-auto w-full" />
          <div className="space-y-4">
            <label className="block font-bold text-blue-950">{tx.price}
              <input value={price} onChange={(e) => setPrice(e.target.value.slice(0, 20))} className="mt-1 w-full rounded-xl border-2 border-yellow-300 p-2" data-testid="poster-price-input" />
            </label>
            <label className="block font-bold text-blue-950">{tx.where}
              <input value={where} onChange={(e) => setWhere(e.target.value.slice(0, 60))} className="mt-1 w-full rounded-xl border-2 border-yellow-300 p-2" data-testid="poster-where-input" />
            </label>
            <div className="flex gap-2">
              {THEMES.map((t) => (
                <button key={t.id} type="button" onClick={() => setThemeId(t.id)} className={`bg-gradient-to-br ${t.bg} rounded-full px-4 py-2 font-bold text-blue-950 border-4 ${t.id === themeId ? "border-blue-950" : "border-white"}`} data-testid={`poster-theme-${t.id}`}>{t[lang] || t.es}</button>
              ))}
            </div>
            <button type="button" onClick={() => window.print()} className="glossy-btn bg-orange-500 text-white font-fredoka font-bold px-6 py-3 rounded-full inline-flex items-center gap-2" data-testid="poster-print-btn">
              <Printer className="w-5 h-5" /> {tx.print}
            </button>
            <p className="text-sm font-semibold text-blue-900/70">{tx.by}</p>
          </div>
        </div>
        {createPortal(<div className="print-area hidden print:flex items-center justify-center bg-white p-6"><Poster {...props} tid="print-poster" className="h-full w-auto max-w-full" /></div>, document.body)}
      </DialogContent>
    </Dialog>
  );
}
